import { useState } from 'react';
import { AlertTriangle, MapPin, Shield, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Pet } from '../../types/pet';
import { reportLost } from '../../../../api/pets';

interface ReportLostModalProps {
  pet: Pet;
  onClose: () => void;
  onSuccess: () => void;
}

export function ReportLostModal({
  pet,
  onClose,
  onSuccess,
}: ReportLostModalProps) {
  const { t } = useTranslation();
  const [lostDescription, setLostDescription] = useState('');
  const [lostLocation, setLostLocation] = useState('');
  const [lostLoading, setLostLoading] = useState(false);

  const handleClose = () => {
    setLostDescription('');
    setLostLocation('');
    onClose();
  };

  const handleSubmit = async () => {
    setLostLoading(true);
    try {
      await reportLost(pet.id, {
        description: lostDescription.trim() || undefined,
        location: lostLocation.trim() || undefined,
      });
      setLostDescription('');
      setLostLocation('');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to report pet as lost:', error);
      alert(
        t(
          'reportLost.submitError',
          'Failed to report pet as lost. Please try again.'
        )
      );
    } finally {
      setLostLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden
      />

      {/* Modal - same as Overview section */}
      <div className="relative bg-white dark:bg-[#16151D] rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden border dark:border-white/10">
        {/* Header with warning icon */}
        <div className="bg-gradient-to-r from-red-500 to-orange-500 p-6 text-white">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-7 h-7" />
            </div>
            <div>
              <h3 className="text-xl font-semibold">
                {t('reportLost.title', 'Report {{name}} as Lost', {
                  name: pet.name,
                })}
              </h3>
              <p className="text-red-100 text-sm">
                {t(
                  'reportLost.subtitle',
                  'Your pet will be searchable in the Global Pet Recovery'
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-700 dark:text-white/80 mb-4">
            {t(
              'reportLost.description',
              'Reporting {{name}} as lost will make them searchable in the Petso Global Pet Recovery system. Anyone who finds your pet can search using their DID or microchip number to contact you.',
              { name: pet.name }
            )}
          </p>

          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-1">
                {t(
                  'reportLost.lastKnownLocation',
                  'Last Known Location (optional)'
                )}
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={lostLocation}
                  onChange={(e) => setLostLocation(e.target.value)}
                  placeholder={t(
                    'reportLost.locationPlaceholder',
                    'e.g., Central Park, New York'
                  )}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-white/20 rounded-lg bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-1">
                {t('reportLost.descriptionLabel', 'Description (optional)')}
              </label>
              <textarea
                value={lostDescription}
                onChange={(e) => setLostDescription(e.target.value)}
                placeholder={t(
                  'reportLost.descriptionPlaceholder',
                  'Any details that might help someone identify your pet...'
                )}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 dark:border-white/20 rounded-lg bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
              />
            </div>
          </div>

          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/30 rounded-lg p-4 mb-6">
            <div className="flex gap-3">
              <Shield className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800 dark:text-amber-200">
                <p className="font-medium mb-1">
                  {t('reportLost.privacyTitle', 'Your privacy is protected')}
                </p>
                <p className="text-amber-700 dark:text-amber-300">
                  {t(
                    'reportLost.privacyBody',
                    'Your contact information will be shared according to your privacy settings. You can update them in Settings.'
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-3 border border-gray-300 dark:border-white/20 text-gray-700 dark:text-white rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors font-medium"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={lostLoading}
              className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {lostLoading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <AlertTriangle className="w-4 h-4" />
              )}
              {t('reportLost.reportAsLost', 'Report as Lost')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
